import { Injectable, DependencyLifeTime } from "@miracledevs/paradigm-web-di";
import mapboxgl, { Map as MapboxMap, MapboxOptions, Style, EaseToOptions, Layer, LngLatLike, EventData, AnySourceData, Source, Marker, AnyLayer, MarkerOptions, PopupOptions, Popup } from "mapbox-gl";
import { ContextCollection } from "../../contexts/DependencyContext";
import { baseOptions } from "./config/base-options";

@Injectable({ collection: ContextCollection, lifeTime: DependencyLifeTime.Singleton })
export class MapService
{
    private _map: MapboxMap;

    private _sources: Map<string, AnySourceData>;

    private _layers: Map<string, AnyLayer | any>;

    private _markers: Map<string | number, Marker>;

    get map(): MapboxMap { return this._map; }

    constructor()
    {
        this._map = null as any;
        this._sources = new Map<string, AnySourceData>();
        this._layers = new Map<string, AnyLayer>();
        this._markers = new Map<string | number, Marker>();
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN as string;
    }

    //#region Map base methods

    /**
     * Sets a map with the provided config
     * @param options - The map initial configuration
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/simple-map/}
     */
    setMap(options?: MapboxOptions): void
    {
        if (!mapboxgl.accessToken)
            throw new Error("Mapbox token wasn't provided...");

        if (!this._map)
            this._map = new MapboxMap({ ...baseOptions, ...options });
    }

    /**
     * Destroys the existant map and it's resources
     */
    destroyMap(): void
    {
        if (this._sources.size) this._sources.clear();
        if (this._layers.size) this._layers.clear();
        if (this._markers.size) this._markers.clear();
        if (this._map) this._map.remove();
        this._map = null as any;
    }

    /**
     * Updates map position
     * @param options - The transition options
     * @param eventData - The event data
     *
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/flyto-options/}
     */
    setPosition(options: EaseToOptions, eventData?: EventData): void
    {
        if (!this._map.isEasing())
            this._map = this._map.easeTo(options, eventData);
    }

    /**
     * Updates map zoom
     * @param zoom - The new zoom value
     * @param eventData - The event data
     */
    setZoom(zoom: number, eventData?: EventData): void
    {
        if (!this._map.isZooming())
            this._map = this._map.setZoom(zoom, eventData);
    }

    /**
     * Updates map theme
     * @param theme - The map theme to use
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/setstyle/}
     */
    setTheme(theme: string | Style): void
    {
        this._map = this._map.setStyle(theme);
    }

    /**
     * Gets map center
     */
    getMapCenter(): LngLatLike
    {
        return this._map.getCenter();
    }

    //#endregion

    //#region Source methods

    /**
     * Gets a specific map source
     * @param id - The source ID
     */
    getSource(id: string): Source
    {
        if (!this._map) return null as any;
        return this._map.getSource(id);
    }

    /**
     * Adds a source to the map
     * @param id - The source ID
     * @param source - The source
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/api/sources/}
     */
    setSource(id: string, source: AnySourceData): void
    {
        const existantSource = this.getSource(id);

        if (!this._map || existantSource) return;

        this._map.once("load", ({ target }: EventData): void =>
        {
            if (target.getSource(id)) return;
            target.addSource(id, source);

            if (!this._sources.get(id))
                this._sources.set(id, source);
        });
    }

    /**
     * Removes a specific map source
     * @param id - The source ID
     */
    removeSource(id: string): void
    {
        if (this.getSource(id))
        {
            this._map = this._map.removeSource(id);
            this._sources.delete(id);
        }
    }

    //#endregion

    //#region Layer methods

    /**
     * Gets a specific map layer
     * @param id - The layer ID
     */
    getLayer(id: string): Layer
    {
        if (!this._map) return null as any;
        return this._map.getLayer(id);
    }

    /**
     * Adds a layer to the map
     * @param id - The layer ID
     * @param layer - The layer options
     * @param before - The ID of a another layer. If the other layer already exists in the stylesheet, the new layer will be positioned right before that layer in the stack
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/}
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/custom-style-layer/}
     */
    setLayer(id: string, layer: AnyLayer, before?: string): void
    {
        const existantLayer = this.getLayer(id);

        if (!this._map || existantLayer) return;

        this._map.once("load", ({ target }: EventData): void =>
        {
            if (target.getSource(id)) return;
            target.addLayer(layer, before);

            if (!this._layers.get(id))
                this._layers.set(id, { ...layer, before });
        });
    }

    /**
     * Removes a specific map layer
     * @param id - The layer ID
     */
    removeLayer(id: string): void
    {
        if (this.getLayer(id))
        {
            this._map = this._map.removeLayer(id);
            this._layers.delete(id);
        }
    }

    /**
     * Toggles a layer"s visibility (visibility property must be provided when the layer is created)
     * @param id - The layer ID
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/example/toggle-layers/}
     */
    toggleLayer(id: string): void
    {
        if (!this.getLayer(id)) return;

        const currentValue = this._map.getLayoutProperty(id, "visibility");
        const visibility = currentValue === "visible" ? "none" : "visible";
        this._map = this._map.setLayoutProperty(id, "visibility", visibility);
    }

    //#endregion

    //#region Marker methods

    /**
     * Sets a marker
     * @param position - The marker's position
     * @param options - The marker's options
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/api/markers/#marker}
     */
    setMarker(element: HTMLElement, id: string | number, position: LngLatLike, options?: MarkerOptions): Marker
    {
        const existantMarker = this._markers.get(id);

        if (existantMarker) return existantMarker;

        const marker = new Marker(element, options).setLngLat(position).addTo(this._map);
        this._markers.set(id, marker);
        return marker;
    }

    /**
     * Gets a specific marker
     * @param id - The marker's ID
     */
    getMarker(id: string | number): Marker | undefined
    {
        return this._markers.get(id);
    }

    /**
     * Removes a marker
     * @param id - The marker's ID
     */
    removeMarker(id: string | number): void
    {
        const marker = this._markers.get(id);

        if (marker)
        {
            marker.remove();
            this._markers.delete(id);
        }
    }

    //#endregion

    //#region Popup methods

    /**
     * Sets a popup
     * @param options - The popup options 
     * @returns The popup
     * @see {@link https://docs.mapbox.com/mapbox-gl-js/api/markers/#popup}
     */
    setPopup(options?: PopupOptions): Popup
    {
        return new Popup(options);
    }

    //#endregion
}
